import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import { AddMarriageDialog } from './AddMarriageDialog';
import { AddParentDialog } from './AddParentDialog';
import { AddChildDialog } from './AddChildDialog';
import type { Person, Marriage, ParentChild } from '@nasabtree/shared-types';

interface RelationshipsPanelProps {
  treeId: string;
  personId: string;
  person: Person | null;
  canEdit: boolean;
  onPersonsChanged: () => void;
  onClose: () => void;
  onViewDetails: () => void;
  onSelectPerson?: (personId: string) => void;
  defaultFamilyName?: string;
}

type PersonPick = { id: string; ism_ar: string | null; ism_en: string | null; display_nasab_ar: string | null };

function PersonLink({ person, onClick }: { person: PersonPick; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm text-brand-600 hover:text-brand-700 font-medium truncate text-start"
      dir="rtl"
    >
      {person.ism_ar ?? person.ism_en ?? '—'}
    </button>
  );
}

export function RelationshipsPanel({
  treeId,
  personId,
  person,
  canEdit,
  onPersonsChanged,
  onClose,
  onViewDetails,
  onSelectPerson,
  defaultFamilyName,
}: RelationshipsPanelProps) {
  const { t } = useTranslation();
  const [marriages, setMarriages] = useState<Marriage[]>([]);
  const [relationships, setRelationships] = useState<ParentChild[]>([]);
  const [loading, setLoading] = useState(true);
  const [addMarriageOpen, setAddMarriageOpen] = useState(false);
  const [addFatherOpen, setAddFatherOpen] = useState(false);
  const [addMotherOpen, setAddMotherOpen] = useState(false);
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const loadRelationships = useCallback(async () => {
    setLoading(true);
    const [mRes, rRes] = await Promise.all([
      apiFetch<Marriage[]>(`/api/trees/${treeId}/persons/${personId}/marriages`),
      apiFetch<ParentChild[]>(`/api/trees/${treeId}/persons/${personId}/relationships`),
    ]);
    if (mRes.ok) setMarriages(mRes.data);
    if (rRes.ok) setRelationships(rRes.data);
    setLoading(false);
  }, [treeId, personId]);

  useEffect(() => {
    void loadRelationships();
  }, [loadRelationships]);

  // Derive parents and children from relationships
  const parents = relationships.filter((r) => r.child_id === personId);
  const children = relationships.filter((r) => r.parent_id === personId);
  const hasFather = parents.some((r) => r.parent_role === 'father');
  const hasMother = parents.some((r) => r.parent_role === 'mother');

  async function removeMarriage(marriageId: string) {
    setRemovingId(marriageId);
    const res = await apiFetch<null>(`/api/trees/${treeId}/marriages/${marriageId}`, { method: 'DELETE' });
    if (res.ok) {
      setMarriages((prev) => prev.filter((m) => m.id !== marriageId));
    }
    setRemovingId(null);
  }

  async function removeRelationship(relId: string) {
    setRemovingId(relId);
    const res = await apiFetch<null>(`/api/trees/${treeId}/relationships/${relId}`, { method: 'DELETE' });
    if (res.ok) {
      setRelationships((prev) => prev.filter((r) => r.id !== relId));
    }
    setRemovingId(null);
  }

  const sectionCls = 'mb-5';
  const sectionHeadCls = 'text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center justify-between';

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-gray-900 truncate" dir="rtl">
            {person?.ism_ar ?? '…'}
          </h2>
          <p className="text-xs text-gray-400">{t('relationships.title')}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onViewDetails}
            className="text-xs text-brand-600 hover:text-brand-700 font-medium px-2 py-1 rounded hover:bg-brand-50 transition-colors"
          >
            {t('persons.viewDetails')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Parents section */}
            <div className={sectionCls}>
              <div className={sectionHeadCls}>
                <span>{t('relationships.parents')}</span>
                {canEdit && (
                  <div className="flex gap-1">
                    {!hasFather && (
                      <button
                        type="button"
                        onClick={() => setAddFatherOpen(true)}
                        className="text-brand-600 hover:text-brand-700 text-xs font-medium"
                      >
                        + {t('parentChild.father')}
                      </button>
                    )}
                    {!hasMother && (
                      <>
                        {!hasFather && <span className="text-gray-300 mx-0.5">|</span>}
                        <button
                          type="button"
                          onClick={() => setAddMotherOpen(true)}
                          className="text-brand-600 hover:text-brand-700 text-xs font-medium"
                        >
                          + {t('parentChild.mother')}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              {parents.length === 0 ? (
                <p className="text-sm text-gray-400 italic">{t('relationships.emptyParents')}</p>
              ) : (
                <div className="space-y-1.5">
                  {parents.map((rel) => (
                    <div key={rel.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-xs text-gray-400 shrink-0 w-8">
                        {rel.parent_role === 'father' ? t('parentChild.father') : t('parentChild.mother')}
                      </span>
                      {rel.parent && <PersonLink person={rel.parent} onClick={() => onSelectPerson?.(rel.parent_id)} />}
                      {rel.relationship_type !== 'biological' && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                          {t(`parentChild.${rel.relationship_type}`)}
                        </span>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => void removeRelationship(rel.id)}
                          disabled={removingId === rel.id}
                          className="ms-auto text-xs text-red-500 hover:text-red-700 shrink-0"
                        >
                          {removingId === rel.id ? '…' : t('relationships.remove')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Spouses section */}
            <div className={sectionCls}>
              <div className={sectionHeadCls}>
                <span>{t('relationships.spouses')}</span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setAddMarriageOpen(true)}
                    className="text-brand-600 hover:text-brand-700 text-xs font-medium"
                  >
                    + {t('relationships.addSpouse')}
                  </button>
                )}
              </div>
              {marriages.length === 0 ? (
                <p className="text-sm text-gray-400 italic">{t('relationships.emptySpouses')}</p>
              ) : (
                <div className="space-y-1.5">
                  {marriages.map((m) => {
                    const spouse = m.husband_id === personId ? m.wife : m.husband;
                    return (
                      <div key={m.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                        {spouse && <PersonLink person={spouse} onClick={() => onSelectPerson?.(spouse.id)} />}
                        <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                          m.status === 'married' ? 'bg-green-50 text-green-700' :
                          m.status === 'divorced' ? 'bg-red-50 text-red-600' :
                          m.status === 'widowed' ? 'bg-gray-100 text-gray-600' :
                          'bg-amber-50 text-amber-700'
                        }`}>
                          {t(`marriage.${m.status}`)}
                        </span>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => void removeMarriage(m.id)}
                            disabled={removingId === m.id}
                            className="ms-auto text-xs text-red-500 hover:text-red-700 shrink-0"
                          >
                            {removingId === m.id ? '…' : t('relationships.remove')}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Children section */}
            <div className={sectionCls}>
              <div className={sectionHeadCls}>
                <span>{t('relationships.children')}</span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setAddChildOpen(true)}
                    className="text-brand-600 hover:text-brand-700 text-xs font-medium"
                  >
                    + {t('relationships.addChild')}
                  </button>
                )}
              </div>
              {children.length === 0 ? (
                <p className="text-sm text-gray-400 italic">{t('relationships.emptyChildren')}</p>
              ) : (
                <div className="space-y-1.5">
                  {children.map((rel) => (
                    <div key={rel.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      {rel.child && <PersonLink person={rel.child} onClick={() => onSelectPerson?.(rel.child_id)} />}
                      {rel.relationship_type !== 'biological' && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                          {t(`parentChild.${rel.relationship_type}`)}
                        </span>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => void removeRelationship(rel.id)}
                          disabled={removingId === rel.id}
                          className="ms-auto text-xs text-red-500 hover:text-red-700 shrink-0"
                        >
                          {removingId === rel.id ? '…' : t('relationships.remove')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Dialogs */}
      {person && (
        <>
          <AddMarriageDialog
            treeId={treeId}
            person={person}
            open={addMarriageOpen}
            onOpenChange={setAddMarriageOpen}
            onCreated={() => void loadRelationships()}
            onPersonsChanged={onPersonsChanged}
            defaultFamilyName={defaultFamilyName}
          />
          <AddParentDialog
            treeId={treeId}
            person={person}
            role="father"
            open={addFatherOpen}
            onOpenChange={setAddFatherOpen}
            onCreated={() => void loadRelationships()}
            onPersonsChanged={onPersonsChanged}
            defaultFamilyName={defaultFamilyName}
          />
          <AddParentDialog
            treeId={treeId}
            person={person}
            role="mother"
            open={addMotherOpen}
            onOpenChange={setAddMotherOpen}
            onCreated={() => void loadRelationships()}
            onPersonsChanged={onPersonsChanged}
            defaultFamilyName={defaultFamilyName}
          />
          <AddChildDialog
            treeId={treeId}
            person={person}
            open={addChildOpen}
            onOpenChange={setAddChildOpen}
            onCreated={() => void loadRelationships()}
            onPersonsChanged={onPersonsChanged}
            defaultFamilyName={defaultFamilyName}
          />
        </>
      )}
    </div>
  );
}
